import {
  BusinessCopy, BusinessThemeTokens, DEFAULT_THEME, defaultCopy, mergeCopy, mergeTheme,
} from '../theme/businessTheme';

export interface BusinessAgent {
  id: string;
  title: string;
  introMessage?: string;
  voiceEnabled: boolean;
}

export interface BusinessLink {
  platform: string;
  url: string;
  label: string;
}

export interface BusinessAddress {
  line1?: string; city?: string; region?: string; postal?: string; country?: string;
}

export interface Business {
  channelId: string;
  /** false when the endpoint failed — defaults are in effect. */
  loaded: boolean;
  displayName: string;
  summary: string;
  category: string;
  about: string;
  hours: string;
  phone: string;
  website: string;
  serviceArea: string;
  address: BusinessAddress | null;
  logoUrl: string | null;
  theme: BusinessThemeTokens;
  copy: BusinessCopy;
  agents: BusinessAgent[];
  /** "Talk to us" target picked by the server (phone-line agent, else top voice agent). */
  talkAgentId: string | null;
  /** Social & review links the owner added (empty when none). */
  links: BusinessLink[];
}

const str = (v: unknown) => (typeof v === 'string' ? v : '');

export function fallbackBusiness(channelId: string): Business {
  return {
    channelId, loaded: false, displayName: '', summary: '', category: '', about: '', hours: '',
    phone: '', website: '', serviceArea: '', address: null, logoUrl: null,
    theme: { ...DEFAULT_THEME }, copy: defaultCopy(''), agents: [], talkAgentId: null, links: [],
  };
}

function normalize(channelId: string, raw: any): Business {
  const displayName = str(raw?.displayName).trim();
  const category = str(raw?.category);
  const agents: BusinessAgent[] = Array.isArray(raw?.agents)
    ? raw.agents
        .filter((a: any) => a && typeof a.id === 'string')
        .map((a: any) => ({
          id: a.id,
          title: str(a.title) || 'Chat',
          introMessage: str(a.introMessage) || undefined,
          voiceEnabled: a.voiceEnabled === true,
        }))
    : [];
  const links: BusinessLink[] = Array.isArray(raw?.links)
    ? raw.links
        .filter((l: any) => l && typeof l.url === 'string' && /^https:\/\//i.test(l.url))
        .map((l: any) => ({ platform: str(l.platform) || 'other', url: l.url, label: str(l.label) || l.url }))
    : [];
  const addr = raw?.address && typeof raw.address === 'object' ? (raw.address as BusinessAddress) : null;
  return {
    channelId,
    loaded: true,
    displayName,
    summary: str(raw?.summary),
    category,
    about: str(raw?.about),
    hours: str(raw?.hours),
    phone: str(raw?.phone),
    website: str(raw?.website),
    serviceArea: str(raw?.serviceArea),
    address: addr && (addr.line1 || addr.city) ? addr : null,
    logoUrl: str(raw?.brand?.logoUrl) || null,
    theme: mergeTheme(raw?.brand?.theme),
    copy: mergeCopy(raw?.brand?.copy, displayName, category),
    agents,
    talkAgentId: str(raw?.talkAgentId) || null,
    links,
  };
}

const cache = new Map<string, Promise<Business>>();

/** Fetch (and memoize for the page session) the public business profile.
    Never rejects — resolves a defaults-only Business on any failure. */
export function fetchBusiness(channelId: string): Promise<Business> {
  const hit = cache.get(channelId);
  if (hit) return hit;
  const p = (async () => {
    try {
      const res = await fetch(`/api/public/business/${encodeURIComponent(channelId)}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return normalize(channelId, await res.json());
    } catch (e) {
      cache.delete(channelId); // allow a retry on the next mount
      return fallbackBusiness(channelId);
    }
  })();
  cache.set(channelId, p);
  return p;
}

export function formatAddress(a: BusinessAddress | null): string {
  if (!a) return '';
  const cityLine = [a.city, [a.region, a.postal].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return [a.line1, cityLine].filter(Boolean).join(', ');
}
